import { Button, Select, SelectItem, Modal, ModalBody, ModalContent, ModalHeader, ModalFooter, Tooltip } from '@nextui-org/react'
import { enqueueSnackbar } from 'notistack'
import { FC, useCallback, useMemo, useRef, useState } from 'react'
import * as XLSX from 'xlsx'
import { MdAssignment, MdLocalShipping, MdStorefront, MdFileDownload, MdAutoAwesome, MdPerson, MdFileOpen, MdRoute, MdWarehouse } from 'react-icons/md' //  MdArrowForward, MdAutorenew
import { IconType } from 'react-icons'
import ActualizarStockModal, { ActualizarStockTarget } from '../ActualizarStockModal'
import ApiStateView from '../ApiState'
// import KpiCard from '../KpiCard' // sección de KPIs comentada en el render
import LogiSearch from '../LogiSearch'
import LogiTable from '../LogiTable'
import SearchInput from '../SearchInput'
import SectionTitle from '../SectionTitle'
import AddressModal from '../AddressModal'
import UploadLabelModal from '../UploadLabelModal'
import { TONES, COURIER, TERMINO_ETIQUETA, TERMINO_ETIQUETA_LOWER } from '../../constants'
import { useLogisticBox } from '../../context/LogisticBoxContext'
import { useApi } from '../../hooks/useApi'
import { useSelectedSeller } from '../../hooks/useSelectedSeller'
import { useStoreScope } from '../../hooks/useStoreScope'
import { useUserId } from '../../hooks/useUserId'
import {
  suppliesApi, labelsApi, InventoryStoreRow, SupplyRequestDto, InventoryCell, SupplyRequestHistory, resolveLabelUrl, fileToBase64, StoreAddressInput
} from '../../api'
import { ToneKey } from '../../models'

// Colores de KPI por insumo (según maqueta v6) · usado solo por la sección de KPIs (comentada)
// const KPI_TONE: Record<string, ToneKey> = { caja: 'beige', bolsa_a: 'teal', bolsa_b: 'green', sello: 'blue' }

// Pill por estado (#12 · sin priorización #5). Incluye mapeo defensivo de
// estados legados (por si la BD aún no corrió la migración v2).
const ESTADO_PILL: Record<string, { tone: ToneKey, label: string }> = {
  pendiente: { tone: 'amber', label: 'Creada' },
  despachada: { tone: 'blue', label: 'En camino' },
  con_error: { tone: 'rosa', label: 'Con error' },
  // legados
  solicitada: { tone: 'amber', label: 'Creada' },
  aprobada: { tone: 'amber', label: 'Creada' },
  rechazada: { tone: 'rosa', label: 'Rechazada' },
  cancelada: { tone: 'rosa', label: 'Rechazada' }
}

const pillFor = (estado: string): { tone: ToneKey, label: string } =>
  ESTADO_PILL[estado] ?? { tone: 'lila', label: estado }

// Tono → clases Ximple para spans de estado.
const toneClasses = (tone: ToneKey): string => {
  switch (tone) {
    case 'green': return 'bg-[#EAF3DE] text-[#3B6D11]'
    case 'amber': return 'bg-[#FEF3C7] text-[#92400E]'
    case 'rosa': return 'bg-[#FAECE7] text-[#993C1D]'
    case 'blue': return 'bg-[#E6F1FB] text-[#185FA5]'
    case 'lila': return 'bg-[#ede9fe] text-[#7c3aed]'
    case 'teal': return 'bg-[#E0F2F1] text-[#0F766E]'
    default: return 'bg-[#f5f5f4] text-[#78716c]'
  }
}

// Finalizada = el envío ya llegó a la tienda (recibido).
const esFinalizada = (s: SupplyRequestDto): boolean => s.envio_entregado

// Estado actual de la solicitud, equivalente al chip que se muestra en la tabla:
//   Entregada · Con error · En camino · Creada.
const estadoActualDe = (s: SupplyRequestDto): SolEstado => {
  if (esFinalizada(s)) return 'entregada'
  if (pillDe(s).tone === 'rosa') return 'con_error'
  if (s.envio_en_camino) return 'en_camino'
  return 'creada'
}

// Pill por estado real del envío:
//   recibido → Entregada · recogido (en tránsito) → En camino ·
//   con etiqueta pero aún sin recoger → Creada · etiqueta fallida → Con error.
const pillDe = (s: SupplyRequestDto): { tone: ToneKey, label: string } => {
  if (s.estado === 'con_error') return { tone: 'rosa', label: 'Con error' }
  if (s.envio_entregado) return { tone: 'green', label: 'Entregada' }
  if (s.envio_en_camino) return { tone: 'blue', label: 'En camino' }
  if (['despachada', 'pendiente', 'solicitada', 'aprobada'].includes(s.estado)) {
    return { tone: 'amber', label: 'Creada' }
  }
  return pillFor(s.estado)
}

// Origen de la solicitud · IA (automática) o Manual (persona)
const esIA = (r: SupplyRequestDto): boolean => r.origen === 'automatica'
const creadorDe = (r: SupplyRequestDto): string =>
  r.origen === 'automatica' ? 'IA' : (r.solicitada_por_nombre ?? `user ${r.solicitada_por_user_id ?? '—'}`)

// Nombre corto por código de insumo · usado en el timeline del historial.
const NOMBRE_INSUMO: Record<string, string> = {
  caja: 'Cajas', bolsa_a: 'Bolsas A', bolsa_b: 'Bolsas B', sello: 'Sellos'
}

const qtyOf = (r: { items: Array<{ codigo: string, cantidad_solicitada: number }> }, codigo: string): number =>
  r.items.find(i => i.codigo === codigo)?.cantidad_solicitada ?? 0

const fmtFecha = (iso: string): string => {
  const d = new Date(iso)
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const hh = String(d.getHours()).padStart(2, '0')
  const mn = String(d.getMinutes()).padStart(2, '0')
  return `${dd}/${mm} · ${hh}:${mn}`
}

// Celda de inventario: valor / safety + chip de porcentaje coloreado
const StockCell: FC<{ cell: InventoryCell | undefined }> = ({ cell }) => {
  if (cell === undefined) return <span className='text-xs text-gray-300'>—</span>
  const tone: ToneKey = cell.bajo_safety ? (cell.porcentaje < 40 ? 'rosa' : 'amber') : 'green'
  const pct = Math.max(0, Math.min(100, cell.porcentaje))
  return (
    <div className='flex items-center gap-2'>
      <span className='font-semibold text-sm' style={{ color: tone === 'green' ? undefined : TONES[tone].ink }}>
        {cell.cantidad_actual} <span className='text-gray-400 font-normal'>/ {cell.safety_stock}</span>
      </span>
      <span
        className='inline-block text-[11px] font-semibold px-2 py-0.5 rounded-full'
        style={{ background: TONES[tone].bg, color: TONES[tone].ink }}
      >
        {pct}%
      </span>
    </div>
  )
}

// Cantidad actual de un insumo en una fila de inventario (para sort).
const invCell = (t: InventoryStoreRow, codigo: string): number =>
  t.celdas.find(c => c.codigo === codigo)?.cantidad_actual ?? -1

// Columnas de cada tabla preview.
const solColumns = [
  { name: 'Fecha', uid: 'fecha', sortBy: (r: SupplyRequestDto) => r.fecha_solicitud },
  { name: 'Tienda', uid: 'store' },
  { name: 'Origen', uid: 'origen', sortBy: (r: SupplyRequestDto) => r.origen },
  { name: 'Tipo', uid: 'tipo_envio', sortBy: (r: SupplyRequestDto) => r.tipo_envio },
  { name: 'Referencia', uid: 'referencia', sortBy: (r: SupplyRequestDto) => r.referencia },
  { name: 'Fecha de envío', uid: 'fecha_envio', sortBy: (r: SupplyRequestDto) => r.fecha_programada_envio },
  { name: 'Cajas', uid: 'caja', align: 'center' as const, sortBy: (r: SupplyRequestDto) => qtyOf(r, 'caja') },
  { name: 'Bolsas A', uid: 'bolsa_a', align: 'center' as const, sortBy: (r: SupplyRequestDto) => qtyOf(r, 'bolsa_a') },
  { name: 'Bolsas B', uid: 'bolsa_b', align: 'center' as const, sortBy: (r: SupplyRequestDto) => qtyOf(r, 'bolsa_b') },
  { name: 'Sellos', uid: 'sello', align: 'center' as const, sortBy: (r: SupplyRequestDto) => qtyOf(r, 'sello') },
  { name: 'Estado', uid: 'estado', sortBy: (r: SupplyRequestDto) => pillDe(r).label },
  { name: '', uid: 'actions' }
]

const invColumns = [
  { name: 'Tienda', uid: 'store' },
  { name: 'Cajas', uid: 'caja', sortBy: (t: InventoryStoreRow) => invCell(t, 'caja') },
  { name: 'Bolsas tipo A', uid: 'bolsa_a', sortBy: (t: InventoryStoreRow) => invCell(t, 'bolsa_a') },
  { name: 'Bolsas tipo B', uid: 'bolsa_b', sortBy: (t: InventoryStoreRow) => invCell(t, 'bolsa_b') },
  { name: 'Sellos', uid: 'sello', sortBy: (t: InventoryStoreRow) => invCell(t, 'sello') },
  { name: 'Último restock', uid: 'restock', sortBy: (t: InventoryStoreRow) => t.ultimo_restock_at },
  { name: 'Alerta', uid: 'alerta', sortBy: (t: InventoryStoreRow) => (t.tiene_alerta ? 1 : 0) },
  { name: '', uid: 'actions' }
]

// Tabs principales · separa las secciones del módulo de insumos.
type MainTab = 'solicitudes' | 'envios' | 'inventario' | 'proyecciones_ia' | 'automaticas'

// Estados actuales de una solicitud · mismos chips que la tabla.
type SolEstado = 'creada' | 'en_camino' | 'entregada' | 'con_error'
type SolFilter = 'todas' | SolEstado

const SOL_FILTERS: Array<{ key: SolFilter, label: string }> = [
  { key: 'todas', label: 'Todas' },
  { key: 'creada', label: 'Creada' },
  { key: 'en_camino', label: 'En camino' },
  { key: 'entregada', label: 'Entregada' },
  { key: 'con_error', label: 'Con error' }
]

const InsumosView: FC = () => {
  const { openSolicitar, goToShipmentTracking, supplyRefresh, setActiveView } = useLogisticBox()
  const idSeller = useSelectedSeller()
  const userId = useUserId()
  const [search, setSearch] = useState('')
  const [solFilter, setSolFilter] = useState<SolFilter>('todas')
  const [solSearch, setSolSearch] = useState('')
  const [mainTab, setMainTab] = useState<MainTab>('inventario')
  const [actualizarTarget, setActualizarTarget] = useState<ActualizarStockTarget | null>(null)
  const [historyTarget, setHistoryTarget] = useState<{ id_store: number, store: string, id_request: number } | null>(null)
  // Etiqueta del envío abierta en modal (iframe) · url + título
  const [etiqueta, setEtiqueta] = useState<{ url: string, titulo: string } | null>(null)

  const summary = useApi(useCallback(async () => await suppliesApi.inventorySummary(idSeller), [idSeller]), [idSeller, supplyRefresh])
  const requests = useApi(useCallback(async () => await suppliesApi.requests(idSeller), [idSeller]), [idSeller, supplyRefresh])
  const shipments = useApi(useCallback(async () => await suppliesApi.shipments(idSeller), [idSeller]), [idSeller, supplyRefresh])
  // Jefe de tienda → solo su tienda · admin → todo el seller (scopeStore null).
  const { scopeStore } = useStoreScope()
  // La tab "Solicitudes" se oculta en la app de tiendas (gestión de admin en arrivals).
  const MOSTRAR_SOLICITUDES = false
  const inventory = useApi(useCallback(async () => await suppliesApi.inventoryByStore(idSeller, scopeStore), [idSeller, scopeStore]), [idSeller, scopeStore, supplyRefresh])

  // Refresca todo lo que cambia tras aprobar/rechazar
  const refreshAll = useCallback(async () => {
    await Promise.all([requests.reload(), shipments.reload(), summary.reload()])
  }, [requests, shipments, summary])

  // Despacho manual eliminado · el despacho es automático (DHL al generar etiqueta,
  // interno al marcarse en el tracking de reparto). Se conserva por si se reactiva.
  // const handleDispatch = useCallback(async (idRequest: number) => {
  //   setBusy(true)
  //   try {
  //     await suppliesApi.dispatchRequest(idRequest, idSeller, userId)
  //     enqueueSnackbar(`Solicitud ${idRequest} despachada`, { variant: 'success' })
  //     await refreshAll()
  //   } catch (e: any) {
  //     enqueueSnackbar(e?.message ?? 'No se pudo despachar', { variant: 'error' })
  //   } finally {
  //     setBusy(false)
  //   }
  // }, [idSeller, userId, refreshAll])

  // Reintentar etiqueta de una solicitud con error (re-dispara DHL/interno).
  const [busyReq, setBusyReq] = useState<number | null>(null)
  const handleRetry = useCallback(async (idRequest: number) => {
    setBusyReq(idRequest)
    try {
      const r = await suppliesApi.retryLabel(idRequest, idSeller, userId)
      enqueueSnackbar(
        r.estado === 'con_error' ? `No se pudo generar: ${r.error_message ?? 'error DHL'}` : `${TERMINO_ETIQUETA} generada`,
        { variant: r.estado === 'con_error' ? 'warning' : 'success' }
      )
      await refreshAll()
    } catch (e: any) {
      enqueueSnackbar(e?.message ?? 'No se pudo reintentar', { variant: 'error' })
    } finally {
      setBusyReq(null)
    }
  }, [idSeller, userId, refreshAll])

  // Agregar dirección a una tienda sin dirección. A diferencia de Recolecciones,
  // aquí NO se genera recolección de cajas: se reintenta la etiqueta del envío
  // de insumos de la solicitud bloqueada.
  const [addrTarget, setAddrTarget] = useState<{ id_store: number, store: string, id_request: number } | null>(null)
  const handleSaveAddress = useCallback(async (input: StoreAddressInput) => {
    const idRequest = addrTarget?.id_request ?? null
    try {
      // Guardar la dirección sin disparar la recolección de cajas.
      await labelsApi.upsertAddress(input, idSeller, userId === 0 ? null : userId, false)
      // Reintentar la etiqueta del envío de insumos ahora que ya hay dirección.
      if (idRequest !== null) {
        const r = await suppliesApi.retryLabel(idRequest, idSeller, userId)
        enqueueSnackbar(
          r.estado === 'con_error'
            ? `Dirección guardada · la ${TERMINO_ETIQUETA_LOWER} aún falla: ${r.error_message ?? 'error DHL'}`
            : `Dirección guardada · ${TERMINO_ETIQUETA_LOWER} generada`,
          { variant: r.estado === 'con_error' ? 'warning' : 'success' }
        )
      } else {
        enqueueSnackbar('Dirección guardada', { variant: 'success' })
      }
      setAddrTarget(null)
      await refreshAll()
    } catch (e: any) {
      enqueueSnackbar(e?.message ?? 'No se pudo guardar la dirección', { variant: 'error' })
    }
  }, [addrTarget, idSeller, userId, refreshAll])

  // Cargar manualmente la etiqueta DHL (PDF) cuando el API falla.
  // Modal "Cargar etiqueta" · pide tracking DHL + PDF
  const [uploadTarget, setUploadTarget] = useState<{ id_request: number, store: string } | null>(null)
  const [uploadingReq, setUploadingReq] = useState(false)
  const handleUploadSupplyLabel = useCallback(async (tracking: string, file: File) => {
    if (uploadTarget === null) return
    setUploadingReq(true)
    try {
      const b64 = await fileToBase64(file)
      const r = await suppliesApi.uploadManualLabel(uploadTarget.id_request, b64, tracking, idSeller, userId)
      enqueueSnackbar(`${TERMINO_ETIQUETA} cargada (${r.tracking_number})`, { variant: 'success' })
      setUploadTarget(null)
      await refreshAll()
    } catch (e: any) {
      enqueueSnackbar(e?.message ?? `No se pudo cargar la ${TERMINO_ETIQUETA_LOWER}`, { variant: 'error' })
    } finally {
      setUploadingReq(false)
    }
  }, [uploadTarget, idSeller, userId, refreshAll])

  // Abre la etiqueta IMPRIMIBLE del envío en un modal (iframe), nunca el tracking:
  //  · DHL con PDF real → muestra el PDF de DHL (etiqueta con código de barras)
  //  · interno o DHL sin PDF → etiqueta imprimible HTML del envío de insumos
  const verEtiqueta = useCallback((r: SupplyRequestDto) => {
    if (r.id_shipment === null) {
      enqueueSnackbar(`Esta solicitud aún no tiene ${TERMINO_ETIQUETA_LOWER} generada`, { variant: 'info' })
      return
    }
    const dhlPdf = resolveLabelUrl(r.pdf_url, idSeller)
    const url = dhlPdf ?? resolveLabelUrl(`/supplies/supplyLabel/${r.id_shipment}`, idSeller)
    if (url !== null) {
      setEtiqueta({ url, titulo: `${r.store} · ${r.referencia ?? r.tracking_number ?? `envío ${r.id_shipment}`}` })
    }
  }, [idSeller, enqueueSnackbar])

  // Tracking del envío · solo disponible una vez despachado.
  const verTracking = useCallback((r: SupplyRequestDto) => {
    if (r.tracking_number !== null) goToShipmentTracking(r.tracking_number)
  }, [goToShipmentTracking])

  // KPIs (6 tarjetas) · sección de tarjetas comentada en el render (ver abajo).
  // Se mantiene comentado el cálculo para no romper el build (noUnusedLocals).
  // const kpis: KpiCardData[] = useMemo(() => {
  //   const s = summary.data
  //   const cards: KpiCardData[] = [
  //     { value: s?.total_tiendas ?? 0, label: 'Tiendas', tone: 'lila', hint: 'Monitoreadas en tiempo real' }
  //   ]
  //   for (const ins of s?.insumos ?? []) {
  //     cards.push({
  //       value: ins.en_tiendas,
  //       label: ins.nombre,
  //       tone: KPI_TONE[ins.codigo] ?? 'beige',
  //       hint: `+ ${ins.en_transito} en tránsito · ${ins.porcentaje_optimo}% óptimo (objetivo ${ins.objetivo_total})`
  //     })
  //   }
  //   cards.push({ value: s?.tiendas_stock_bajo ?? 0, label: 'Tiendas stock bajo', tone: 'rosa', hint: 'Con algún insumo bajo safety' })
  //   return cards
  // }, [summary.data])

  // Solicitudes filtradas por el tab activo · los finalizados (entregados +
  // rechazados) salen solo en su tab y se excluyen de los demás.
  const solCounts = useMemo(() => {
    const all = requests.data ?? []
    const c = { todas: all.length, creada: 0, en_camino: 0, entregada: 0, con_error: 0 }
    all.forEach(r => { c[estadoActualDe(r)]++ })
    return c
  }, [requests.data])

  const solFiltradas = useMemo(() => {
    const q = solSearch.toLowerCase()
    const base = (requests.data ?? []).filter(r =>
      `${r.store} ${r.referencia ?? ''} ${r.tracking_number ?? ''}`.toLowerCase().includes(q)
    )
    if (solFilter === 'todas') return base
    return base.filter(r => estadoActualDe(r) === solFilter)
  }, [requests.data, solFilter, solSearch])

  // Exporta a Excel las solicitudes actualmente filtradas.
  const handleExportSolicitudes = useCallback(() => {
    if (solFiltradas.length === 0) { enqueueSnackbar('No hay solicitudes para exportar', { variant: 'info' }); return }
    const out = solFiltradas.map(s => ({
      Fecha: fmtFecha(s.fecha_solicitud),
      Tienda: s.store,
      'Tiene dirección': s.tiene_direccion ? 'Sí' : 'No',
      Origen: s.origen === 'automatica' ? 'Automática' : 'Manual',
      Solicitó: creadorDe(s),
      Gestionó: s.gestionada_por_nombre ?? '',
      Cajas: qtyOf(s, 'caja'),
      'Bolsas A': qtyOf(s, 'bolsa_a'),
      'Bolsas B': qtyOf(s, 'bolsa_b'),
      Sellos: qtyOf(s, 'sello'),
      Estado: pillDe(s).label
    }))
    const now = new Date()
    const pad = (n: number): string => String(n).padStart(2, '0')
    const stamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(out), 'Solicitudes')
    XLSX.writeFile(wb, `Solicitudes_insumos_${stamp}.xlsx`)
    enqueueSnackbar(`Excel exportado · ${solFiltradas.length} solicitudes`, { variant: 'success' })
  }, [solFiltradas])

  // Inventario filtrado por búsqueda
  const inventario: InventoryStoreRow[] = useMemo(
    () => (inventory.data ?? []).filter(i => i.store.toLowerCase().includes(search.toLowerCase())),
    [inventory.data, search]
  )

  const cellOf = (row: InventoryStoreRow, codigo: string): InventoryCell | undefined =>
    row.celdas.find(c => c.codigo === codigo)

  // Sugerencia para el modal: lo que falta para llegar a 1.5× safety
  const sugerirCantidad = (cell: InventoryCell | undefined): number => {
    if (cell === undefined || !cell.bajo_safety) return 0
    return Math.max(0, Math.ceil(cell.safety_stock * 1.5) - cell.cantidad_actual)
  }

  // Solicitudes (preview)
  const renderSolCell = (r: SupplyRequestDto, columnKey: string): JSX.Element => {
    switch (columnKey) {
      case 'fecha':
        return <span className='text-xs'>{fmtFecha(r.fecha_solicitud)}</span>
      case 'store':
        return (
          <button
            type='button'
            className='font-medium text-xs text-neutral-600 hover:underline text-left'
            onClick={() => setHistoryTarget({ id_store: r.id_store, store: r.store, id_request: r.id_request })}
          >
            {r.store}
          </button>
        )
      case 'origen':
        return esIA(r)
          ? (
            <span className='inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#ede9fe] text-[#7c3aed]'>
              <MdAutoAwesome className='text-sm' />IA
            </span>
            )
          : (
            <div>
              <span className='inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#f5f5f4] text-[#78716c]'>
                <MdPerson className='text-sm' />Manual
              </span>
              {r.solicitada_por_nombre !== null && <div className='text-[10px] text-gray-400 mt-0.5'>{r.solicitada_por_nombre}</div>}
            </div>
            )
      case 'tipo_envio':
        if (r.tipo_envio === 'interno') {
          return (
            <span className='inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#ede9fe] text-[#7c3aed]'>
              <MdWarehouse className='text-sm' />Reuse
            </span>
          )
        }
        if (r.tipo_envio === 'dhl') {
          return (
            <span className='inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#E6F1FB] text-[#185FA5]'>
              <MdLocalShipping className='text-sm' />{COURIER}
            </span>
          )
        }
        return <span className='text-xs text-gray-400'>—</span>
      case 'referencia':
        return (
          <span className='text-xs font-mono text-neutral-700'>
            {r.referencia ?? <span className='text-gray-400'>—</span>}
          </span>
        )
      case 'fecha_envio':
        return <span className='text-xs text-[#151930]/70'>{r.fecha_programada_envio !== null ? fmtFecha(r.fecha_programada_envio) : '—'}</span>
      case 'caja':
        return <span className='text-xs'>{qtyOf(r, 'caja') || '—'}</span>
      case 'bolsa_a':
        return <span className='text-xs'>{qtyOf(r, 'bolsa_a') || '—'}</span>
      case 'bolsa_b':
        return <span className='text-xs'>{qtyOf(r, 'bolsa_b') || '—'}</span>
      case 'sello':
        return <span className='text-xs'>{qtyOf(r, 'sello') || '—'}</span>
      case 'estado': {
        const pill = pillDe(r)
        return (
          <div>
            <span className={`inline-block text-[11px] font-medium px-2 py-0.5 rounded-full ${toneClasses(pill.tone)}`}>{pill.label}</span>
            {r.gestionada_por_nombre !== null && (
              <div className='text-[10px] text-gray-400 mt-0.5'>por {r.gestionada_por_nombre}</div>
            )}
          </div>
        )
      }
      case 'actions': {
        const tieneEtiqueta = r.id_shipment !== null
        const despachada = r.estado === 'despachada'
        const conError = r.estado === 'con_error'
        if (conError && !r.tiene_direccion) {
          // Sin dirección: el único botón es agregar dirección (como en Recolecciones).
          return (
            <div className='flex justify-end whitespace-nowrap'>
              <Button
                size='sm' className='bg-[#44526B] text-white text-xs h-8'
                onPress={() => setAddrTarget({ id_store: r.id_store, store: r.store, id_request: r.id_request })}
              >
                Agregar dirección
              </Button>
            </div>
          )
        }
        if (conError) {
          // Error de DHL (con dirección): reintentar o cargar una etiqueta manual.
          return (
            <div className='flex gap-2 justify-end items-center whitespace-nowrap'>
              <Button
                size='sm' className='bg-[#44526B] text-white text-xs h-8'
                isLoading={busyReq === r.id_request}
                onPress={() => { void handleRetry(r.id_request) }}
              >
                Reintentar
              </Button>
              <Button
                size='sm' variant='bordered' className='border-[#44526B]/30 text-[#44526B] text-xs h-8'
                onPress={() => setUploadTarget({ id_request: r.id_request, store: r.store })}
              >
                Cargar {TERMINO_ETIQUETA_LOWER}
              </Button>
            </div>
          )
        }
        return (
          <div className='flex gap-2 justify-end items-center whitespace-nowrap'>
            <Button
              size='sm' variant='bordered' className='border-[#44526B]/30 text-[#44526B] text-xs h-8'
              isDisabled={!tieneEtiqueta}
              onPress={() => verEtiqueta(r)}
            >
              Ver {TERMINO_ETIQUETA_LOWER}
            </Button>
            {/* Botón "Despachar" comentado · el despacho es automático:
                DHL al generar la etiqueta, e interno al marcarse en el tracking de reparto.
            {!despachada && (
              <Button
                size='sm' className='bg-[#44526B] text-white text-xs h-8'
                isDisabled={busy || !tieneEtiqueta}
                startContent={<MdLocalShipping className='text-sm' />}
                onPress={() => { void handleDispatch(r.id_request) }}
              >
                Despachar
              </Button>
            )} */}
            {/* Tracking: siempre que esté despachada, y también en los internos con
                etiqueta aunque sigan pendientes (para poder gestionar el reparto). */}
            {(despachada || (r.tipo_envio === 'interno' && r.tracking_number !== null)) && (
              <Button
                size='sm' variant='bordered' className='border-[#44526B]/30 text-[#44526B] text-xs h-8'
                isDisabled={r.tracking_number === null}
                onPress={() => verTracking(r)}
              >
                Tracking
              </Button>
            )}
          </div>
        )
      }
      default:
        return <span className='text-xs'>{(r as any)[columnKey]}</span>
    }
  }

  // Inventario por tienda (preview)
  const renderInvCell = (t: InventoryStoreRow, columnKey: string): JSX.Element => {
    switch (columnKey) {
      case 'store':
        return <span className='font-medium text-xs'>{t.store}</span>
      case 'caja':
        return <StockCell cell={cellOf(t, 'caja')} />
      case 'bolsa_a':
        return <StockCell cell={cellOf(t, 'bolsa_a')} />
      case 'bolsa_b':
        return <StockCell cell={cellOf(t, 'bolsa_b')} />
      case 'sello':
        return <StockCell cell={cellOf(t, 'sello')} />
      case 'restock':
        return <span className='text-xs text-neutral-500'>{t.ultimo_restock_at !== null ? fmtFecha(t.ultimo_restock_at) : '—'}</span>
      case 'alerta':
        return (
          <span className={`inline-block text-[11px] font-medium px-2 py-0.5 rounded-full ${t.tiene_alerta ? 'bg-[#FAECE7] text-[#993C1D]' : 'bg-[#EAF3DE] text-[#3B6D11]'}`}>
            {t.tiene_alerta ? 'Stock bajo' : 'OK'}
          </span>
        )
      case 'actions':
        return (
          <div className='flex gap-2 justify-end items-center whitespace-nowrap'>
            <Button
              size='sm' variant='bordered' className='border-[#44526B]/30 text-[#44526B] text-xs h-8'
              onPress={() => setActualizarTarget({
                idStore: t.id_store,
                tienda: t.store,
                stock: {
                  caja: cellOf(t, 'caja')?.cantidad_actual ?? 0,
                  bolsa_a: cellOf(t, 'bolsa_a')?.cantidad_actual ?? 0,
                  bolsa_b: cellOf(t, 'bolsa_b')?.cantidad_actual ?? 0,
                  sello: cellOf(t, 'sello')?.cantidad_actual ?? 0
                }
              })}
            >
              Actualizar
            </Button>
            <Button
              size='sm' className='bg-[#44526B] text-white text-xs h-8'
              onPress={() => openSolicitar({
                tienda: t.store,
                idStore: t.id_store,
                cajas: sugerirCantidad(cellOf(t, 'caja')),
                bolsasA: sugerirCantidad(cellOf(t, 'bolsa_a')),
                bolsasB: sugerirCantidad(cellOf(t, 'bolsa_b')),
                sellos: sugerirCantidad(cellOf(t, 'sello'))
              })}
            >
              Solicitar insumos
            </Button>
          </div>
        )
      default:
        return <span className='text-xs'>{(t as any)[columnKey]}</span>
    }
  }

  return (
    <div className='space-y-4'>
      {/* KPIs · 6 tarjetas */}
      {/* <section className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4'>
        {kpis.map((k, i) => <KpiCard key={i} data={k} />)}
      </section> */}

      {/* Un solo card: sidebar izquierdo + contenido derecho */}
      <section className='bg-white border border-neutral-200 rounded-xl overflow-hidden'>
        <div className='flex'>
          {/* Sub-menú vertical */}
          <aside className='w-[190px] flex-shrink-0 flex flex-col gap-1 p-3 border-r border-neutral-100'>
            {([
              { key: 'inventario', label: 'Inventario', count: inventory.data?.length ?? 0, Icon: MdStorefront },
              ...(MOSTRAR_SOLICITUDES ? [{ key: 'solicitudes', label: 'Solicitudes y envíos', count: solCounts.todas, Icon: MdAssignment }] : [])
            ] as Array<{ key: MainTab, label: string, count?: number, Icon: IconType }>).map(t => {
              const active = mainTab === t.key
              return (
                <Tooltip key={t.key} content={t.label} placement='right' size='sm'>
                  <button
                    onClick={() => setMainTab(t.key)}
                    className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl text-sm transition-colors ${active ? 'bg-[#44526B] text-white font-semibold' : 'text-neutral-500 hover:bg-neutral-100 font-medium'}`}
                  >
                    <span className='flex items-center gap-2 min-w-0'>
                      <t.Icon className='text-base shrink-0' />
                      <span className='truncate'>{t.label}</span>
                    </span>
                    {t.count !== undefined && (
                      <span className={`shrink-0 text-[12px] leading-none px-1.5 py-0.5 rounded-full font-semibold ${active ? 'bg-white/20 text-white' : 'bg-neutral-200/70 text-neutral-500'}`}>
                        {t.count}
                      </span>
                    )}
                  </button>
                </Tooltip>
              )
            })}
          </aside>

          {/* Contenido del tab activo */}
          <div className='flex-1 min-w-0'>

            {/* Solicitudes de insumos */}
            {mainTab === 'solicitudes' && (
              <>
                <div className='flex items-center justify-between flex-wrap gap-3 px-5 pt-5 pb-3'>
                  <SectionTitle title='Solicitudes y envíos de insumos' description='Creadas, gestionadas, en camino y entregadas' />
                  <div className='flex items-center gap-2 flex-wrap'>
                    <Select
                      aria-label='Filtrar solicitudes por estado'
                      size='sm'
                      selectedKeys={[solFilter]}
                      disallowEmptySelection
                      selectionMode='single'
                      className='w-48'
                      onSelectionChange={(keys) => {
                        const k = Array.from(keys)[0]
                        if (k !== undefined) setSolFilter(k as SolFilter)
                      }}
                    >
                      {SOL_FILTERS.map(f => (
                        <SelectItem key={f.key} textValue={`${f.label} (${solCounts[f.key]})`}>
                          {f.label} <span className='opacity-60'>({solCounts[f.key]})</span>
                        </SelectItem>
                      ))}
                    </Select>
                    <LogiSearch value={solSearch} onChange={setSolSearch} placeholder='Buscar tienda o referencia…' className='w-56' />
                    <Button
                      size='sm' className='bg-[#44526B] text-white text-xs'
                      startContent={<MdRoute className='text-base' />}
                      onPress={() => setActiveView('reparto-interno')}
                    >
                      Reparto Reuse
                    </Button>
                    <Button
                      variant='bordered' size='sm' className='border-gray-200 text-xs'
                      startContent={<MdFileDownload className='text-base' />}
                      isDisabled={requests.loading || solFiltradas.length === 0}
                      onPress={handleExportSolicitudes}
                    >
                      Exportar
                    </Button>
                  </div>
                </div>
                <div className='px-5 pb-5'>
                  <ApiStateView
                    loading={requests.loading} error={requests.error} onRetry={requests.reload}
                    empty={!requests.loading && solFiltradas.length === 0} emptyText='Sin solicitudes en esta vista'
                  >
                    <LogiTable
                      columns={solColumns}
                      items={solFiltradas}
                      renderCell={renderSolCell}
                      rowKey={(r) => r.id_request}
                      ariaLabel='Solicitudes de insumos'
                      loading={requests.loading}
                      emptyText='Sin solicitudes en esta vista'
                      rowsPerPage={10}
                      bottomExtra={
                        <div className='text-xs text-neutral-500'>
                          {solFiltradas.length} solicitudes · filtro: {SOL_FILTERS.find(f => f.key === solFilter)?.label}
                        </div>
                      }
                    />
                  </ApiStateView>
                </div>
              </>
            )}

            {/* Inventario por tienda */}
            {mainTab === 'inventario' && (
              <>
                <div className='flex items-center justify-between flex-wrap gap-3 px-5 pt-5 pb-3'>
                  <SectionTitle
                    title='Inventario actual por tienda'
                    description='Safety stock · Cajas: 2 · Bolsas A: 50 · Bolsas B: 30 · Sellos: 20'
                  />
                  <div className='flex items-center gap-2 flex-wrap'>
                    <div className='w-56'>
                      <SearchInput placeholder='Buscar tienda…' value={search} onChange={setSearch} />
                    </div>
                  </div>
                </div>
                <div className='px-5 pb-5'>
                  <ApiStateView
                    loading={inventory.loading} error={inventory.error} onRetry={inventory.reload}
                    empty={!inventory.loading && inventario.length === 0} emptyText='Sin inventario cargado'
                  >
                    <LogiTable
                      columns={invColumns}
                      items={inventario}
                      renderCell={renderInvCell}
                      rowKey={(t) => t.id_store}
                      ariaLabel='Inventario actual por tienda'
                      loading={inventory.loading}
                      emptyText='Sin inventario cargado'
                      rowsPerPage={10}
                      bottomExtra={
                        <div className='flex items-center justify-between text-xs text-neutral-500 flex-wrap gap-3'>
                          <span>Mostrando {inventario.length} de {inventory.data?.length ?? 0} tiendas</span>
                          <div className='flex gap-4'>
                            <span className='flex items-center gap-1'><span className='w-2 h-2 rounded-full' style={{ background: '#3B6D11' }} />Stock óptimo</span>
                            <span className='flex items-center gap-1'><span className='w-2 h-2 rounded-full' style={{ background: '#92400E' }} />Stock bajo</span>
                            <span className='flex items-center gap-1'><span className='w-2 h-2 rounded-full' style={{ background: '#993C1D' }} />Stock crítico</span>
                          </div>
                        </div>
                      }
                    />
                  </ApiStateView>
                </div>
              </>
            )}

          </div>
        </div>
      </section>

      {/* Modal · actualizar stock físico de una tienda */}
      <ActualizarStockModal
        target={actualizarTarget}
        onClose={() => setActualizarTarget(null)}
        onSaved={() => { void summary.reload(); void inventory.reload() }}
      />

      {/* Modal · historial de solicitudes de la tienda */}
      <HistorialSolicitudModal
        target={historyTarget}
        idSeller={idSeller}
        onClose={() => setHistoryTarget(null)}
      />

      {/* Modal · etiqueta imprimible del envío (iframe) */}
      <EtiquetaModal etiqueta={etiqueta} onClose={() => setEtiqueta(null)} />

      {/* Modal · agregar dirección a una tienda sin dirección */}
      <AddressModal target={addrTarget} onClose={() => setAddrTarget(null)} onSave={handleSaveAddress} confirmLabel={`Crear ${TERMINO_ETIQUETA_LOWER}`} />

      {/* Modal · cargar etiqueta DHL manual (tracking + PDF) */}
      <UploadLabelModal
        open={uploadTarget !== null}
        store={uploadTarget?.store}
        busy={uploadingReq}
        onClose={() => setUploadTarget(null)}
        onSubmit={(tracking, file) => { void handleUploadSupplyLabel(tracking, file) }}
      />
    </div>
  )
}

/* Modal que muestra la etiqueta del envío (HTML interna o PDF de DHL) en un
 * iframe, con botones para imprimir y abrir en pestaña. */
const EtiquetaModal: FC<{
  etiqueta: { url: string, titulo: string } | null
  onClose: () => void
}> = ({ etiqueta, onClose }) => {
  const iframeRef = useRef<HTMLIFrameElement | null>(null)
  if (etiqueta === null) return null

  const imprimir = (): void => {
    try {
      iframeRef.current?.contentWindow?.focus()
      iframeRef.current?.contentWindow?.print()
    } catch {
      window.open(etiqueta.url, '_blank')
    }
  }

  return (
    <Modal isOpen={etiqueta !== null} onClose={onClose} size='3xl' scrollBehavior='inside'>
      <ModalContent>
        <ModalHeader className='flex flex-col gap-0'>
          <span className='text-[10px] uppercase tracking-widest text-neutral-400 font-bold'>{TERMINO_ETIQUETA} del envío</span>
          <span className='text-base font-bold text-neutral-800'>{etiqueta.titulo}</span>
        </ModalHeader>
        <ModalBody className='pb-2'>
          <div className='w-full rounded-lg overflow-hidden border border-neutral-200 bg-neutral-50'>
            <iframe
              ref={iframeRef}
              src={etiqueta.url}
              title={`${TERMINO_ETIQUETA} del envío`}
              className='w-full'
              style={{ height: '60vh', border: 'none' }}
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant='flat' className='text-xs' onPress={() => window.open(etiqueta.url, '_blank')}>
            Abrir en pestaña
          </Button>
          <Button className='bg-[#44526B] text-white text-xs' startContent={<MdFileOpen className='text-sm' />} onPress={imprimir}>
            Imprimir
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

const TIPO_EVENTO: Record<string, { label: string, cls: string }> = {
  creada: { label: 'Creada', cls: 'bg-[#ede9fe] text-[#7c3aed]' },
  editada: { label: 'Editada', cls: 'bg-[#E6F1FB] text-[#185FA5]' },
  etiqueta: { label: TERMINO_ETIQUETA, cls: 'bg-[#f5f5f4] text-[#78716c]' },
  despachada: { label: 'En camino', cls: 'bg-[#E6F1FB] text-[#185FA5]' },
  error: { label: 'Error', cls: 'bg-[#FAECE7] text-[#993C1D]' }
}

const HistorialSolicitudModal: FC<{
  target: { id_store: number, store: string, id_request: number } | null
  idSeller: number | null
  onClose: () => void
}> = ({ target, idSeller, onClose }) => {
  const data = useApi<SupplyRequestHistory | null>(
    useCallback(async () => {
      if (target === null) return null
      return await suppliesApi.requestHistory(target.id_store, idSeller, target.id_request)
    }, [target, idSeller]),
    [target, idSeller]
  )

  if (target === null) return null
  const eventos = data.data?.eventos ?? []

  return (
    <Modal isOpen={target !== null} onClose={onClose} size='2xl' scrollBehavior='inside'>
      <ModalContent>
        <ModalHeader className='flex flex-col gap-0'>
          {/* <span className='text-[10px] uppercase tracking-widest text-neutral-400 font-bold'>Historial de solicitudes</span> */}
          <span className='text-base font-bold text-neutral-800'>{target.store}</span>
        </ModalHeader>
        <ModalBody className='gap-5 pb-6'>
          <ApiStateView loading={data.loading} error={data.error} onRetry={data.reload}>
            {/* Timeline de la solicitud actual */}
            <div>
              <h4 className='text-xs font-bold uppercase tracking-wide text-neutral-400 mb-2'>Cambios de la solicitud actual</h4>
              {eventos.length === 0
                ? <p className='text-xs text-neutral-400'>Sin eventos registrados.</p>
                : (
                  <ol className='relative'>
                    {eventos.map((e, i) => {
                      const meta = TIPO_EVENTO[e.tipo] ?? { label: e.tipo, cls: 'bg-[#f5f5f4] text-[#78716c]' }
                      const last = i === eventos.length - 1
                      return (
                        <li key={e.id} className='relative pl-6 pb-4 last:pb-0'>
                          {!last && <span className='absolute left-[5px] top-3 bottom-0 w-[2px] bg-neutral-100' />}
                          <span className='absolute left-0 top-1 w-2.5 h-2.5 rounded-full bg-neutral-600' />
                          <div className='flex items-center gap-2 flex-wrap'>
                            <span className={`inline-block text-[11px] font-medium px-2 py-0.5 rounded-full ${meta.cls}`}>{meta.label}</span>
                            {e.origen !== null && <span className='text-[10px] uppercase text-neutral-400'>{e.origen === 'automatica' ? 'IA' : 'Manual'}</span>}
                            <span className='text-[11px] text-neutral-400'>{fmtFecha(e.fecha)}{e.por !== null ? ` · ${e.por}` : ''}</span>
                          </div>
                          {e.descripcion !== null && <div className='text-xs text-neutral-600 mt-0.5'>{e.descripcion}</div>}
                          {e.items !== null && e.items.length > 0 && (
                            <div className='flex flex-wrap gap-1.5 mt-1.5'>
                              {e.items.map(it => (
                                <span key={it.codigo} className='text-[11px] px-1.5 py-0.5 rounded-md bg-neutral-100 text-neutral-600 font-medium'>
                                  {NOMBRE_INSUMO[it.codigo] ?? it.codigo}: <b>{it.cantidad}</b>
                                </span>
                              ))}
                            </div>
                          )}
                        </li>
                      )
                    })}
                  </ol>
                  )}
            </div>

            {/* Solicitudes históricas de la tienda */}
            {/* <div>
              <h4 className='text-xs font-bold uppercase tracking-wide text-[#151930]/55 mb-2'>Solicitudes de la tienda</h4>
              <div className='space-y-2'>
                {solicitudes.map(s => (
                  <div key={s.id_request} className='flex items-center justify-between gap-2 border border-[#eef0f7] rounded-lg px-3 py-2'>
                    <div className='text-xs'>
                      <div className='font-medium text-[#151930]'>{fmtFecha(s.fecha_solicitud)} · {esIA(s) ? 'IA' : 'Manual'}</div>
                      <div className='text-[11px] text-[#151930]/55'>
                        {['caja', 'bolsa_a', 'bolsa_b', 'sello'].map(c => `${c === 'caja' ? 'Cajas' : c === 'bolsa_a' ? 'B.A' : c === 'bolsa_b' ? 'B.B' : 'Sellos'} ${qtyOf(s, c)}`).join(' · ')}
                      </div>
                    </div>
                    <Chip size='sm' variant='flat' color={chipColor(pillDe(s).tone)}>{pillDe(s).label}</Chip>
                  </div>
                ))}
              </div>
            </div> */}
          </ApiStateView>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export default InsumosView
